import { supabase } from '../lib/supabase';
import { fixEncoding } from '../utils/textCleaner';

// Transform snake_case to camelCase for consistent JS naming
const toCamelCase = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(toCamelCase);
  }
  
  const camelCased = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelCased[camelKey] = toCamelCase(value);
  }
  return camelCased;
};

export const bibleService = {
  // Get all Bible versions
  async getBibleVersions() {
    try {
      let { data, error } = await supabase
        ?.from('bible_versions')
        ?.select('id, version_name, short_name, language, description, is_enabled, has_strongs')
        ?.order('version_name');

      // If has_strongs column doesn't exist yet, fall back to query without it
      if (error && error?.code === '42703') {
        console.warn('has_strongs column not found, falling back to query without it');
        const fallback = await supabase
          ?.from('bible_versions')?.select('id, version_name, short_name, language, description, is_enabled');
        data = (fallback?.data || [])?.map(v => ({ ...v, has_strongs: false }));
        error = fallback?.error;
      }

      if (error) {
        console.error('Bible versions query error:', error);
        return { data: [], error };
      }
      
      if (!data || data?.length === 0) {
        console.warn('No Bible versions found in database');
        return { 
          data: [], 
          error: { message: 'No Bible versions found. Please import Bible data.' } 
        };
      }

      // Fix encoding for version names (French and other accented languages)
      const fixedData = data?.map(v => ({
        ...v,
        version_name: fixEncoding(v?.version_name),
        short_name: fixEncoding(v?.short_name),
        description: fixEncoding(v?.description),
      }));
      
      return { data: fixedData, error: null };
    } catch (error) {
      console.error('Bible versions network error:', error);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get all Bible books with improved error handling
  async getBibleBooks(testament = null) {
    try {
      let query = supabase
        ?.from('bible_books')
        ?.select('*')
        ?.order('book_number');

      if (testament && testament !== 'all') {
        query = query?.eq('testament', testament);
      }

      const { data, error } = await query;
      
      if (error) {
        console.error('Bible books query error:', error);
        return { data: [], error };
      }
      
      // Ensure we have data
      if (!data || data?.length === 0) {
        console.warn('No Bible books found in database');
        return { 
          data: [], 
          error: { message: 'No Bible books found. Please check database migration.' } 
        };
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Bible books network error:', error);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get localized book name for a given book number and language
  async getLocalizedBookName(bookNumber, language) {
    try {
      if (!language || language?.toLowerCase() === 'english') return null;
      const { data, error } = await supabase?.rpc('get_localized_book_name', {
        p_book_number: bookNumber,
        p_language: language
      });
      if (error) return null;
      return data || null;
    } catch {
      return null;
    }
  },

  // Get chapters for a specific book
  async getBookChapters(bookId) {
    try {
      // Use the new database function for better reliability
      const { data, error } = await supabase?.rpc('get_book_chapters', {
        book_id_param: bookId
      });

      if (error) {
        console.error('Error fetching book chapters:', error);
        return { data: null, error };
      }

      // Transform the data to ensure proper camelCase naming
      const transformedData = data?.map(chapter => ({
        id: chapter?.chapter_id,
        chapterNumber: chapter?.chapter_number,
        verseCount: chapter?.verse_count,
        bookId: chapter?.book_id
      }));

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error fetching book chapters:', err);
      return { data: null, error: err };
    }
  },

  // Get verses for a specific chapter with version support
  async getChapterVerses(bookName, chapterNumber, versionShortName = 'kjv') {
    try {
      if (!bookName || !chapterNumber) {
        return { data: [], error: { message: 'Book name and chapter number are required.' } };
      }

      // Use the updated function with version parameter
      const { data, error } = await supabase?.rpc('get_chapter_verses', {
          book_name_param: bookName,
          chapter_num: chapterNumber,
          version_short_name: versionShortName?.toUpperCase()
        });

      if (error) {
        console.error('Chapter verses query error:', error);
        return { data: [], error };
      }
      
      // Check if we got results
      if (!data || data?.length === 0) {
        console.warn(`No verses found for ${bookName} ${chapterNumber} in ${versionShortName}`);
        return { 
          data: [], 
          error: { message: `No verses found for ${bookName} chapter ${chapterNumber} in ${versionShortName?.toUpperCase()}. This version may not be available for this chapter.` } 
        };
      }

      // Transform the data to include version information
      const transformedVerses = data?.map(verse => ({
        verse_id: verse?.verse_id,
        verse_number: verse?.verse_number,
        text: fixEncoding(verse?.text),
        reference: verse?.reference,
        version_id: verse?.version_id,
        version_name: fixEncoding(verse?.version_name)
      }));

      console.log(`Loaded ${transformedVerses?.length} verses for ${bookName} ${chapterNumber} (${versionShortName?.toUpperCase()})`);
      return { data: transformedVerses, error: null };
    } catch (error) {
      console.error('Chapter verses network error:', error);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  },

  // Search Bible verses by text content using the comprehensive search function
  async searchVerses(searchQuery, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.rpc('search_bible_comprehensive', {
          search_term: searchQuery
        });

      if (error) {
        console.error('Error searching verses:', error);
        return { data: [], error };
      }

      // Transform the data to match expected format
      const transformedData = data?.map(verse => ({
        verse_id: verse?.verse_id,
        verse_number: verse?.verse_number,
        text: verse?.verse_text,
        reference: verse?.reference,
        book_name: verse?.book_name,
        chapter_number: verse?.chapter_number,
        rank: verse?.rank
      })) || [];

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error searching verses:', err);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get a specific verse by reference
  async getVerseByReference(bookName, chapterNumber, verseNumber) {
    try {
      const { data, error } = await supabase?.from('bible_verses')?.select(`
          *,
          bible_versions!inner(short_name),
          bible_chapters!inner(
            chapter_number,
            bible_books!inner(name, abbreviation)
          )
        `)
        ?.eq('bible_chapters.bible_books.name', bookName)
        ?.eq('bible_chapters.chapter_number', chapterNumber)
        ?.eq('verse_number', verseNumber)
        ?.eq('bible_versions.short_name', 'KJV')
        ?.maybeSingle();

      if (error) {
        console.error('Error fetching verse by reference:', error);
        return { data: null, error };
      }

      if (!data) {
        return { data: null, error: null };
      }

      // Transform the data
      const transformedData = {
        verse_id: data?.id,
        verse_number: data?.verse_number,
        text: data?.text,
        reference: data?.reference,
        book_name: data?.bible_chapters?.bible_books?.name,
        book_abbreviation: data?.bible_chapters?.bible_books?.abbreviation,
        chapter_number: data?.bible_chapters?.chapter_number
      };

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error fetching verse by reference:', err);
      return { data: null, error: err };
    }
  },

  // Get random verses using database function
  async getRandomVerses(count = 1) {
    try {
      const { data, error } = await supabase?.rpc('get_random_verse');

      if (error) {
        console.error('Error fetching random verses:', error);
        return { data: [], error };
      }

      // Transform to match expected format
      const transformedData = Array.isArray(data) ? data : [data];
      const result = transformedData?.map(verse => ({
        verse_id: verse?.verse_id,
        verse_number: verse?.verse_number,
        text: verse?.verse_text,
        reference: verse?.reference,
        book_name: verse?.book_name,
        chapter_number: verse?.chapter_number
      }));

      return { data: result, error: null };
    } catch (err) {
      console.error('Network error fetching random verses:', err);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get verse context (surrounding verses)
  async getVerseContext(bookName, chapterNumber, verseNumber, contextSize = 2) {
    try {
      const startVerse = Math.max(1, verseNumber - contextSize);
      const endVerse = verseNumber + contextSize;

      const { data, error } = await supabase?.from('bible_verses')?.select(`
          *,
          bible_chapters!inner(
            chapter_number,
            bible_books!inner(name, abbreviation)
          )
        `)?.eq('bible_chapters.bible_books.name', bookName)?.eq('bible_chapters.chapter_number', chapterNumber)?.gte('verse_number', startVerse)?.lte('verse_number', endVerse)?.order('verse_number');

      if (error) {
        console.error('Error fetching verse context:', error);
        return { data: null, error };
      }

      // Transform the data
      const transformedData = data?.map(verse => ({
        verse_id: verse?.id,
        verse_number: verse?.verse_number,
        text: verse?.text,
        reference: verse?.reference,
        book_name: verse?.bible_chapters?.bible_books?.name,
        book_abbreviation: verse?.bible_chapters?.bible_books?.abbreviation,
        chapter_number: verse?.bible_chapters?.chapter_number,
        is_target: verse?.verse_number === verseNumber
      }));

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error fetching verse context:', err);
      return { data: null, error: err };
    }
  },

  // Get verse text for a range of verses (e.g., Psalm 8:1-5)
  async getVerseRangeText(bookName, chapterNumber, startVerse, endVerse, versionShortName = 'kjv') {
    try {
      if (!bookName || !chapterNumber || !startVerse) {
        return { data: [], error: { message: 'Book name, chapter number, and start verse are required.' } };
      }

      // If no end verse, just get single verse
      const finalEndVerse = endVerse || startVerse;

      // Query the bible_verses table for the range
      const { data, error } = await supabase
        ?.from('bible_verses')
        ?.select(`
          id,
          verse_number,
          text,
          reference,
          bible_versions!inner(short_name),
          bible_chapters!inner(
            chapter_number,
            bible_books!inner(name, abbreviation)
          )
        `)
        ?.eq('bible_chapters.bible_books.name', bookName)
        ?.eq('bible_chapters.chapter_number', chapterNumber)
        ?.eq('bible_versions.short_name', versionShortName?.toUpperCase())
        ?.gte('verse_number', startVerse)
        ?.lte('verse_number', finalEndVerse)
        ?.order('verse_number');

      if (error) {
        console.error('Error fetching verse range:', error);
        return { data: [], error };
      }

      if (!data || data?.length === 0) {
        console.warn(`No verses found for ${bookName} ${chapterNumber}:${startVerse}-${finalEndVerse} in ${versionShortName}`);
        return { 
          data: [], 
          error: { message: `Verses not found for ${bookName} ${chapterNumber}:${startVerse}-${finalEndVerse}` } 
        };
      }

      // Transform the data
      const transformedData = data?.map(verse => {
        // Construct reference if it's null in the database
        const bookAbbr = verse?.bible_chapters?.bible_books?.abbreviation || verse?.bible_chapters?.bible_books?.name;
        const chapter = verse?.bible_chapters?.chapter_number;
        let verseNum = verse?.verse_number;
        const constructedReference = `${bookAbbr} ${chapter}:${verseNum}`;
        
        return {
          verseId: verse?.id,
          verseNumber: verse?.verse_number,
          text: verse?.text,
          reference: verse?.reference || constructedReference, // Use database reference or construct it
          bookName: verse?.bible_chapters?.bible_books?.name,
          bookAbbreviation: verse?.bible_chapters?.bible_books?.abbreviation,
          chapterNumber: verse?.bible_chapters?.chapter_number
        };
      });

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error fetching verse range:', err);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// VERSE LIBRARY SERVICE (NEW)
// =============================================================================

export const verseLibraryService = {
  // Get user's verse library for specific book/chapter
  async getUserVerseLibrary(userId, bookName = null, chapterNumber = null) {
    try {
      if (!userId) {
        return { data: [], error: { message: 'User ID is required.' } };
      }

      let query = supabase?.from('verses')?.select('id, book, chapter, verse_start, verse_end, current_phase, reference, text, status')?.eq('user_id', userId);

      if (bookName) {
        // Normalize book name for consistent matching
        const normalizedBook = bookName?.charAt(0)?.toUpperCase() + bookName?.slice(1)?.toLowerCase();
        query = query?.eq('book', normalizedBook);
      }

      if (chapterNumber) {
        query = query?.eq('chapter', chapterNumber);
      }

      const { data, error } = await query?.order('book')?.order('chapter')?.order('verse_start');

      if (error) {
        console.error('Error fetching user verse library:', error);
        return { data: [], error };
      }

      // Transform to camelCase for React
      const transformedData = (data || [])?.map(verse => ({
        id: verse?.id,
        book: verse?.book,
        chapter: verse?.chapter,
        verseStart: verse?.verse_start,
        verseEnd: verse?.verse_end,
        currentPhase: verse?.current_phase,
        reference: verse?.reference,
        text: verse?.text,
        status: verse?.status
      }));

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error fetching verse library:', err);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get verse library map for highlighting (optimized for UI)
  async getVerseLibraryMap(userId, bookName, chapterNumber) {
    try {
      const { data, error } = await this.getUserVerseLibrary(userId, bookName, chapterNumber);
      
      if (error) {
        return { data: new Map(), error };
      }

      // Create a map of verse numbers to phase data
      const libraryMap = new Map();
      
      data?.forEach(verse => {
        const startVerse = verse?.verseStart;
        const endVerse = verse?.verseEnd || startVerse;
        
        // For ranges like "Genesis 1:1-3", mark verses 1, 2, and 3
        for (let verseNum = startVerse; verseNum <= endVerse; verseNum++) {
          libraryMap?.set(verseNum, {
            phase: verse?.currentPhase,
            reference: verse?.reference,
            isRange: startVerse !== endVerse,
            rangeStart: startVerse,
            rangeEnd: endVerse,
            status: verse?.status,
            verseId: verse?.id
          });
        }
      });

      return { data: libraryMap, error: null };
    } catch (err) {
      console.error('Network error creating verse library map:', err);
      return { data: new Map(), error: { message: 'Network error. Please try again.' } };
    }
  },

  // Add verse to library (create new memorization entry)
  async addVerseToLibrary(userId, verseData) {
    try {
      if (!userId) {
        return { data: null, error: { message: 'User authentication required.' } };
      }

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated.' } };
      }

      // Convert camelCase to snake_case for database
      const insertData = {
        user_id: user?.id,
        book: verseData?.book,
        chapter: verseData?.chapter,
        verse_start: verseData?.verseStart,
        verse_end: verseData?.verseEnd || verseData?.verseStart,
        reference: verseData?.reference,
        text: verseData?.text,
        bible_version: verseData?.version?.toLowerCase() || 'kjv',
        current_phase: 1, // Start at Phase 1
        status: 'new',
        notes: verseData?.notes || ''
      };

      const { data, error } = await supabase?.from('verses')?.insert(insertData)?.select()?.single();

      if (error) {
        console.error('Error adding verse to library:', error);
        return { data: null, error };
      }

      // Transform back to camelCase
      const transformedData = {
        id: data?.id,
        book: data?.book,
        chapter: data?.chapter,
        verseStart: data?.verse_start,
        verseEnd: data?.verse_end,
        currentPhase: data?.current_phase,
        reference: data?.reference,
        text: data?.text,
        status: data?.status,
        bibleVersion: data?.bible_version
      };

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error adding verse to library:', err);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Remove verse from library
  async removeVerseFromLibrary(userId, verseId) {
    try {
      if (!userId) {
        return { error: { message: 'User authentication required.' } };
      }

      const { error } = await supabase?.from('verses')?.delete()?.eq('id', verseId)?.eq('user_id', userId);

      if (error) {
        console.error('Error removing verse from library:', error);
        return { error };
      }

      return { error: null };
    } catch (err) {
      console.error('Network error removing verse from library:', err);
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  // Update verse phase or other properties
  async updateVerse(userId, verseId, updates) {
    try {
      if (!userId) {
        return { data: null, error: { message: 'User authentication required.' } };
      }

      // Convert camelCase to snake_case for database
      const dbUpdates = {};
      Object.keys(updates)?.forEach(key => {
        switch (key) {
          case 'currentPhase':
            dbUpdates.current_phase = updates?.[key];
            break;
          case 'verseStart':
            dbUpdates.verse_start = updates?.[key];
            break;
          case 'verseEnd':
            dbUpdates.verse_end = updates?.[key];
            break;
          case 'bibleVersion':
            dbUpdates.bible_version = updates?.[key];
            break;
          default:
            dbUpdates[key] = updates?.[key];
        }
      });

      const { data, error } = await supabase?.from('verses')?.update(dbUpdates)?.eq('id', verseId)?.eq('user_id', userId)?.select()?.single();

      if (error) {
        console.error('Error updating verse:', error);
        return { data: null, error };
      }

      // Transform back to camelCase
      const transformedData = {
        id: data?.id,
        book: data?.book,
        chapter: data?.chapter,
        verseStart: data?.verse_start,
        verseEnd: data?.verse_end,
        currentPhase: data?.current_phase,
        reference: data?.reference,
        text: data?.text,
        status: data?.status,
        bibleVersion: data?.bible_version
      };

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error updating verse:', err);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// COMMENTARY SERVICES (READY FOR FUTURE INTEGRATION)
// =============================================================================

export const commentaryService = {
  // Get SDA Commentary for a verse
  async getSDACommentary(verseId) {
    try {
      const { data, error } = await supabase?.from('sda_commentaries')?.select(`
          *,
          bible_verses (
            reference,
            text
          )
        `)?.eq('verse_id', verseId);

      return { data: data || [], error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get Ellen White Comments for a verse - UPDATED TO USE egw_commentary TABLE
  async getEllenWhiteComments(bookName, chapterNumber, verseNumber) {
    try {
      if (!bookName || !chapterNumber || !verseNumber) {
        return { data: [], error: { message: 'Book, chapter, and verse are required.' } };
      }

      // Query egw_commentary table directly using book_name, chapter, and verse
      const { data, error } = await supabase
        ?.from('egw_commentary')
        ?.select('*')
        ?.ilike('book_name', bookName) // Case-insensitive book name match
        ?.eq('chapter', chapterNumber)
        ?.or(`verse_start.eq.${verseNumber},and(verse_start.lte.${verseNumber},verse_end.gte.${verseNumber})`) // Match single verse or verse ranges
        ?.order('verse_start');

      if (error) {
        console.error('EGW commentary query error:', error);
        return { data: [], error };
      }

      // Handle no results gracefully
      if (!data || data?.length === 0) {
        console.warn(`No EGW commentary found for ${bookName} ${chapterNumber}:${verseNumber}`);
        return { 
          data: [], 
          error: null // Not an error, just no commentary available
        };
      }

      // Transform data to camelCase for React
      const transformedData = data?.map(comment => ({
        id: comment?.id,
        bookName: comment?.book_name,
        chapter: comment?.chapter,
        verseStart: comment?.verse_start,
        verseEnd: comment?.verse_end,
        verseReference: comment?.verse_reference,
        commentaryText: comment?.commentary_text,
        createdAt: comment?.created_at
      }));

      console.log(`Loaded ${transformedData?.length} EGW commentary entries for ${bookName} ${chapterNumber}:${verseNumber}`);
      return { data: transformedData, error: null };
    } catch (error) {
      console.error('EGW commentary network error:', error);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  },

  // Check if EGW commentary exists for a verse - NEW METHOD
  async hasEGWCommentary(bookName, chapterNumber, verseNumber) {
    try {
      const { data, error } = await supabase
        ?.from('egw_commentary')
        ?.select('id')
        ?.ilike('book_name', bookName)
        ?.eq('chapter', chapterNumber)
        ?.or(`verse_start.eq.${verseNumber},and(verse_start.lte.${verseNumber},verse_end.gte.${verseNumber})`)
        ?.limit(1);

      if (error) {
        return { hasCommentary: false, error };
      }

      return { hasCommentary: data && data?.length > 0, error: null };
    } catch (error) {
      return { hasCommentary: false, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Check if commentary exists for a reference
  async hasCommentary(reference) {
    try {
      // First get the Bible verse ID
      const { data: verseData } = await bibleService?.getVerseByReference(reference);
      
      if (!verseData?.verse_id) {
        return { hasCommentary: false, error: null };
      }

      // Check both commentary types
      const [sdaResult, egwResult] = await Promise.all([
        supabase?.from('sda_commentaries')?.select('id')?.eq('verse_id', verseData?.verse_id)?.limit(1),
        supabase?.from('ellen_white_comments')?.select('id')?.eq('verse_id', verseData?.verse_id)?.limit(1)
      ]);

      const hasCommentary = (sdaResult?.data?.length > 0) || (egwResult?.data?.length > 0);
      
      return { hasCommentary, error: null };
    } catch (error) {
      return { hasCommentary: false, error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// READING PLAN INTEGRATION
// =============================================================================

export const bibleReadingService = {
  // Create reading plan with Bible references
  async createBibleReadingPlan(userId, planData) {
    try {
      const { data, error } = await supabase?.from('reading_plans')?.insert({
          created_by: userId,
          name: planData?.name,
          description: planData?.description,
          duration_days: planData?.durationDays,
          category: planData?.category || 'bible_study',
          is_featured: planData?.isFeatured || false,
          is_active: true
        })?.select()?.single();

      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get Bible-specific reading plans
  async getBibleReadingPlans(category = 'bible_study') {
    try {
      const { data, error } = await supabase?.from('reading_plans')?.select(`
          *,
          reading_plan_readings (
            day_number,
            title,
            references
          )
        `)?.eq('category', category)?.eq('is_active', true)?.order('created_at', { ascending: false });

      return { data: data || [], error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// BIBLE STUDY TOOLS
// =============================================================================

export const bibleStudyService = {
  // Get cross-references for a verse (placeholder for future enhancement)
  async getCrossReferences(reference) {
    try {
      // This would be enhanced with actual cross-reference data
      // For now, return related verses by similar words
      const { data: verse } = await bibleService?.getVerseByReference(reference);
      
      if (!verse?.text) {
        return { data: [], error: null };
      }

      // Extract key words (simple implementation)
      const words = verse?.text?.split(' ')?.filter(word => 
        word?.length > 4 && 
        !['that', 'with', 'have', 'this', 'will', 'from', 'they', 'been', 'were', 'said']?.includes(word?.toLowerCase())
      );

      if (words?.length === 0) {
        return { data: [], error: null };
      }

      const searchTerm = words?.[0]?.replace(/[^a-zA-Z]/g, '');
      const { data, error } = await bibleService?.searchVerses(searchTerm, 5);

      // Filter out the original verse
      const filtered = data?.filter(v => v?.reference !== reference) || [];

      return { data: filtered, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get topic-based verse collections
  async getTopicVerses(topic) {
    try {
      const topicKeywords = {
        love: ['love', 'beloved', 'charity'],
        faith: ['faith', 'believe', 'trust'],
        hope: ['hope', 'eternal', 'promise'],
        salvation: ['salvation', 'saved', 'eternal life'],
        peace: ['peace', 'comfort', 'rest'],
        strength: ['strength', 'strong', 'mighty'],
        wisdom: ['wisdom', 'understanding', 'knowledge'],
        prayer: ['pray', 'prayer', 'ask']
      };

      const keywords = topicKeywords?.[topic?.toLowerCase()] || [topic];
      const allVerses = [];

      for (const keyword of keywords) {
        const { data } = await bibleService?.searchVerses(keyword, 10);
        allVerses?.push(...(data || []));
      }

      // Remove duplicates and limit results
      const uniqueVerses = allVerses?.filter((verse, index, self) => 
        index === self?.findIndex(v => v?.reference === verse?.reference)
      )?.slice(0, 20);

      return { data: uniqueVerses, error: null };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// STRONG'S DEFINITIONS SERVICE
// =============================================================================

export const strongsService = {
  // Get Strong's definition by number
  async getStrongsDefinition(strongsNumber) {
    try {
      if (!strongsNumber) {
        return { data: null, error: { message: 'Strong\'s number is required.' } };
      }

      // Clean the number (remove curly braces if present)
      const cleanNumber = strongsNumber?.replace(/[{}]/g, '');

      // Traditional Strong's uses non-padded Hebrew numbers (H216, not H0216)
      // Strip leading zeros from Hebrew numbers for the strongs_definitions table
      const lookupNumber = cleanNumber?.replace(/^(H)0*(\d+)([A-Za-z]*)$/, '$1$2$3');

      const { data, error } = await supabase
        ?.from('strongs_definitions')
        ?.select('*')
        ?.eq('number', lookupNumber);

      if (error) {
        console.error('Strong\'s definition query error:', error);
        return { data: null, error };
      }

      // Handle no results gracefully
      if (!data || data?.length === 0) {
        return { 
          data: null, 
          error: { message: `No definition found for Strong's number ${cleanNumber}` } 
        };
      }

      // Get the first result (should be only one anyway)
      const result = data?.[0];

      // Transform the data to camelCase
      const transformedData = {
        id: result?.id,
        number: result?.number,
        rootWord: result?.root_word,
        transliteration: result?.transliteration,
        pronunciation: result?.pronunciation,
        entry: result?.entry,
        tvm: result?.tvm
      };

      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Strong\'s definition network error:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get multiple Strong's definitions
  async getMultipleStrongsDefinitions(strongsNumbers) {
    try {
      if (!strongsNumbers || strongsNumbers?.length === 0) {
        return { data: [], error: null };
      }

      // Clean the numbers (remove curly braces if present)
      const cleanNumbers = strongsNumbers?.map(num => num?.replace(/[{}]/g, ''));

      const { data, error } = await supabase
        ?.from('strongs_definitions')
        ?.select('*')
        ?.in('number', cleanNumbers);

      if (error) {
        console.error('Multiple Strong\'s definitions query error:', error);
        return { data: [], error };
      }

      // Transform the data to camelCase
      const transformedData = (data || [])?.map(item => ({
        id: item?.id,
        number: item?.number,
        rootWord: item?.root_word,
        transliteration: item?.transliteration,
        pronunciation: item?.pronunciation,
        entry: item?.entry,
        tvm: item?.tvm
      }));

      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Multiple Strong\'s definitions network error:', error);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// TREASURY OF SCRIPTURE KNOWLEDGE (TSK) SERVICE
// =============================================================================

export const tskService = {
  // Get TSK cross-references for a specific verse
  async getTSKForVerse(bookName, chapterNumber, verseNumber) {
    try {
      if (!bookName || !chapterNumber || !verseNumber) {
        return { data: [], error: { message: 'Book, chapter, and verse are required.' } };
      }

      // Log the incoming parameters for debugging
      console.log('TSK Service - getTSKForVerse called with:', { 
        bookName, 
        bookNameType: typeof bookName,
        bookNameLength: bookName?.length,
        chapterNumber, 
        verseNumber 
      });

      // STEP 1: Get the book_id (integer) from bible_books table using book name
      const { data: bookData, error: bookError } = await supabase
        ?.from('bible_books')
        ?.select('id')
        ?.ilike('name', bookName)
        ?.single();

      if (bookError || !bookData) {
        console.error('Error fetching book ID for TSK:', bookError);
        console.error('Book name that failed:', bookName);
        return { 
          data: [], 
          error: { message: `Could not find book: ${bookName}` } 
        };
      }

      // STEP 2: Use the integer book_id to query TSK table
      const { data, error } = await supabase
        ?.from('tsk')
        ?.select('*')
        ?.eq('book_id', bookData?.id)
        ?.eq('chapter', chapterNumber)
        ?.eq('verse', verseNumber)
        ?.order('entry_order');

      if (error) {
        console.error('TSK query error:', error);
        return { data: [], error };
      }

      // Handle no results gracefully
      if (!data || data?.length === 0) {
        return { 
          data: [], 
          error: null // Not an error, just no TSK data available
        };
      }

      // Transform the data to camelCase
      const transformedData = data?.map(item => ({
        id: item?.id,
        bookId: item?.book_id,
        chapter: item?.chapter,
        verse: item?.verse,
        phrase: item?.phrase,
        order: item?.entry_order,
        references: item?.references
      }));

      return { data: transformedData, error: null };
    } catch (error) {
      console.error('TSK network error:', error);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  },

  // Check if TSK data exists for a verse
  async hasTSKData(bookName, chapterNumber, verseNumber) {
    try {
      // STEP 1: Get the book_id (integer) from bible_books table
      const { data: bookData, error: bookError } = await supabase
        ?.from('bible_books')
        ?.select('id')
        ?.ilike('name', bookName)
        ?.single();

      if (bookError || !bookData) {
        return { hasTSK: false, error: bookError };
      }

      // STEP 2: Check TSK table using integer book_id
      const { data, error } = await supabase
        ?.from('tsk')
        ?.select('book_id as id')
        ?.eq('book_id', bookData?.id) // Use integer book_id
        ?.eq('chapter', chapterNumber)
        ?.eq('verse', verseNumber)
        ?.limit(1);

      if (error) {
        return { hasTSK: false, error };
      }

      if (data?.length < 1){
        console.error("no results were found in the DB when checking" + bookData?.id + " chapter:" + chapterNumber +" verse:" +verseNumber);
        return { hasTSK: false, error};
      }

      return { hasTSK: data && data?.length > 0, error: null };
    } catch (error) {
      return { hasTSK: false, error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// VERSE MEMORIZATION & SPACED REPETITION SERVICE
// =============================================================================

export const verseService = {
  // Process verse review with Good/Hard/Missed options
  async processVerseReview(verseId, reviewResult, userId) {
    try {
      if (!verseId || !reviewResult || !userId) {
        return { 
          data: null, 
          error: { message: 'Verse ID, review result, and user ID are required.' } 
        };
      }

      // Validate review result
      const validResults = ['good', 'hard', 'missed'];
      if (!validResults?.includes(reviewResult?.toLowerCase())) {
        return { 
          data: null, 
          error: { message: 'Review result must be "good", "hard", or "missed".' } 
        };
      }

      // Call database function to process review
      const { data, error } = await supabase?.rpc('process_verse_review', {
        p_verse_id: verseId,
        p_review_result: reviewResult?.toLowerCase(),
        p_user_id: userId
      });

      if (error) {
        console.error('Error processing verse review:', error);
        return { data: null, error };
      }

      // Transform response to camelCase
      const transformedData = {
        success: data?.success,
        verseId: data?.verse_id,
        oldPhase: data?.old_phase,
        newPhase: data?.new_phase,
        successfulReviewsInPhase: data?.successful_reviews_in_phase,
        consecutiveSuccessfulReviews: data?.consecutive_successful_reviews,
        nextReviewDate: data?.next_review_date,
        reviewIntervalDays: data?.review_interval_days,
        phaseChanged: data?.phase_changed
      };

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error processing verse review:', err);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get verses due for review (with grace period support)
  async getVersesDueForReview(userId, reviewDate = null) {
    try {
      if (!userId) {
        return { data: [], error: { message: 'User ID is required.' } };
      }

      const dateParam = reviewDate || new Date()?.toISOString()?.split('T')?.[0];

      const { data, error } = await supabase?.rpc('get_verses_due_for_review', {
        p_user_id: userId,
        p_review_date: dateParam
      });

      if (error) {
        console.error('Error fetching verses due for review:', error);
        return { data: [], error };
      }

      // Transform to camelCase
      const transformedData = (data || [])?.map(verse => ({
        verseId: verse?.verse_id,
        reference: verse?.reference,
        text: verse?.text,
        currentPhase: verse?.current_phase,
        successfulReviewsInPhase: verse?.successful_reviews_in_phase,
        consecutiveSuccessfulReviews: verse?.consecutive_successful_reviews,
        nextReviewDate: verse?.next_review_date,
        daysOverdue: verse?.days_overdue,
        verseCount: verse?.verse_count
      }));

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error fetching verses due for review:', err);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get user phase statistics
  async getUserPhaseStatistics(userId) {
    try {
      if (!userId) {
        return { data: null, error: { message: 'User ID is required.' } };
      }

      const { data, error } = await supabase?.rpc('get_user_phase_statistics', {
        p_user_id: userId
      });

      if (error) {
        console.error('Error fetching phase statistics:', error);
        return { data: null, error };
      }

      // Transform to camelCase
      const transformedData = {
        phase0Future: data?.phase_0_future || 0,
        phase1Daily: data?.phase_1_daily || 0,
        phase2Every3Days: data?.phase_2_every_3_days || 0,
        phase3Weekly: data?.phase_3_weekly || 0,
        phase4Biweekly: data?.phase_4_biweekly || 0,
        phase5Monthly: data?.phase_5_monthly || 0,
        phase645Days: data?.phase_6_45_days || 0,
        phase7Quarterly: data?.phase_7_quarterly || 0,
        phase8Mature: data?.phase_8_mature || 0,
        totalActiveVerses: data?.total_active_verses || 0,
        totalVerses: data?.total_verses || 0,
        versesDueToday: data?.verses_due_today || 0,
        versesOverdue: data?.verses_overdue || 0
      };

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error fetching phase statistics:', err);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get phase name and description
  getPhaseInfo(phaseNumber) {
    const phaseInfo = {
      0: { name: 'Future', description: 'Not yet in active review', interval: 'N/A', color: 'gray' },
      1: { name: 'Phase 1', description: 'Daily reviews', interval: '1 day', color: 'red' },
      2: { name: 'Phase 2', description: 'Every 3 days', interval: '3 days', color: 'orange' },
      3: { name: 'Phase 3', description: 'Weekly reviews', interval: '7 days', color: 'yellow' },
      4: { name: 'Phase 4', description: 'Biweekly reviews', interval: '14 days', color: 'green' },
      5: { name: 'Phase 5', description: 'Monthly reviews', interval: '30 days', color: 'blue' },
      6: { name: 'Phase 6', description: 'Every 45 days', interval: '45 days', color: 'indigo' },
      7: { name: 'Phase 7', description: 'Quarterly reviews', interval: '90 days', color: 'purple' },
      8: { name: 'Mature', description: 'Biannual reviews', interval: '180 days', color: 'pink' }
    };

    return phaseInfo?.[phaseNumber] || phaseInfo?.[0];
  }
};